import React from "react";
import { Link } from "react-router-dom";
import { CoachAvatar } from "@/components/CoachAvatar";
import { WelcomeVideo } from "@/components/WelcomeVideo";
import { ArrowRight, Heart, Compass, Sprout } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FDFBF7] grain overflow-x-hidden">
      {/* nav */}
      <nav className="max-w-7xl mx-auto px-5 md:px-10 py-4 md:py-6 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="landing-logo">
          <CoachAvatar size="sm" />
          <div className="leading-tight">
            <div className="font-serif-display text-xl text-[#1C221D]">India Sadie</div>
            <div className="hidden sm:block font-serif-display italic text-xs text-[#5C6A61]">Live the life you've always wanted.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            data-testid="nav-login"
            className="px-3 md:px-4 py-2 text-sm whitespace-nowrap text-[#1C221D] hover:text-[#2A3B32]"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            data-testid="nav-register"
            className="px-5 py-2 rounded-full bg-[#2A3B32] text-[#FDFBF7] text-sm hover:-translate-y-0.5 active:scale-95 transition-transform duration-200"
          >
            Begin
          </Link>
        </div>
      </nav>

      {/* hero */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-4 md:pt-20 pb-16 md:pb-24">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-7">
            <div className="eyebrow text-[#E07A5F] mb-4 md:mb-6">your real-time coach</div>
            <h1 className="font-serif-display font-light text-4xl sm:text-6xl lg:text-7xl text-[#1C221D] leading-[1.05]">
              A quieter kind of
              <br />
              <span className="italic text-[#2A3B32]">clarity</span>, right when
              <br />
              you need it.
            </h1>
            <p className="mt-5 md:mt-8 text-base md:text-lg text-[#5C6A61] leading-relaxed max-w-xl">
              Ask anything — career crossroads, a tough conversation, a late-night spiral.
              India answers in real time with the warmth of a certified coach who's actually listening.
            </p>
            <div className="mt-7 md:mt-10 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
              <Link
                to="/register"
                data-testid="hero-cta-register"
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#2A3B32] text-[#FDFBF7] text-base hover:-translate-y-0.5 active:scale-95 transition-transform duration-200"
              >
                Start your first conversation
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/login"
                data-testid="hero-cta-login"
                className="text-center px-7 py-3 sm:py-3.5 rounded-full border border-[#2A3B32]/15 text-base text-[#1C221D] hover:bg-[#F2EBE5] transition-colors duration-200"
              >
                I already have an account
              </Link>
            </div>
          </div>

          <div className="md:col-span-5">
            <WelcomeVideo />
          </div>
        </div>
      </section>

      {/* value tiles */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        <div className="eyebrow text-[#5C6A61] mb-6">what you'll find inside</div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Heart,
              title: "Real-time advice",
              body: "Word-by-word responses that feel like a human coach thinking alongside you.",
            },
            {
              icon: Compass,
              title: "Tagged by life area",
              body: "Career, relationships, wellness, mindset — each conversation finds its shelf.",
            },
            {
              icon: Sprout,
              title: "Goals that grow",
              body: "Turn insight into movement. Track progress on the goals that actually matter.",
            },
          ].map((t) => (
            <div
              key={t.title}
              className="bg-white rounded-2xl p-7 border border-[#2A3B32]/10 shadow-[0_8px_30px_rgba(42,59,50,0.04)]"
            >
              <div className="w-11 h-11 rounded-full bg-[#F2EBE5] flex items-center justify-center text-[#2A3B32]">
                <t.icon size={20} />
              </div>
              <div className="font-serif-display text-2xl mt-5 text-[#1C221D]">{t.title}</div>
              <p className="text-[#5C6A61] mt-2 leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#2A3B32]/8 py-8 text-center text-xs text-[#5C6A61]">
        India Sadie is a reflective companion, not a substitute for medical or crisis care.
      </footer>
    </div>
  );
}
